// "Order raised" email feedback, shared by Raise Transfer Order, Production
// Approval (Sales Order raised) and the Transfer Order / Sales Order screens.
// The backend sends the mail as a background task, so the create/approve
// response only says "pending" + who it is going to; watchOrderEmail polls
// the order's email_status until the send has finished, then shows the
// result toast. The order itself is already saved either way.
import api from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 2000;
const MAX_POLLS = 8;

const copyNote = (copyTo, myEmail) => {
  if (!copyTo) return "";
  return copyTo.toLowerCase() === (myEmail || "").toLowerCase() ? " (copy sent to you)" : ` (copy sent to ${copyTo})`;
};

// One-line summary of an order's email_* fields (also used for the badge).
export const orderEmailSummary = (o, myEmail) => {
  const to = (o?.email_recipients || []).join(", ");
  const copyTo = o?.email_copy_to;
  switch (o?.email_status) {
    case "sent":
      return to ? `Emailed to ${to}${copyNote(copyTo, myEmail)}` : `Copy emailed to ${copyTo}`;
    case "queued":
      return `Email queued for ${to || copyTo}`;
    case "pending":
      return `Emailing ${to || copyTo}…`;
    case "failed":
      return `Email failed${o.email_error ? `: ${o.email_error}` : ""}`;
    case "skipped":
      return "No email recipients configured";
    default:
      return "";
  }
};

// Toast for a finished send. `res` is the order (email_* fields) or a
// send_order_email() result ({status, recipients, copy_to, error, warning}).
export const toastOrderEmailResult = (label, res, myEmail) => {
  const status = res.email_status || res.status;
  const to = (res.email_recipients || res.recipients || []).join(", ");
  const copyTo = res.email_copy_to ?? res.copy_to;
  const warning = res.email_warning ?? res.warning;
  const error = res.email_error ?? res.error;
  if (status === "sent" || status === "queued") {
    const verb = status === "sent" ? "emailed to" : "queued for email to";
    const msg = to ? `${label} raised and ${verb} ${to}${copyNote(copyTo, myEmail)}`
      : `${label} raised — confirmation ${status === "sent" ? "emailed" : "queued"} to ${copyTo}`;
    if (warning) toast.warning(msg, { description: warning });
    else toast.success(msg);
  } else if (status === "failed") {
    toast.warning(`${label} saved, but the email could not be sent`, {
      description: `${error || "Unknown error"} — use “Resend email” on the order.`,
    });
  } else if (status === "skipped") {
    toast.warning(`${label} saved — no email recipients configured`, warning ? { description: warning } : undefined);
  }
};

// Poll `path` (GET returns the order) until its email has been sent or has
// failed, then toast the result. Fire-and-forget: safe to call right before
// navigating away, since toasts are global.
export const watchOrderEmail = async (path, label, email, myEmail) => {
  if (!email) return;
  if (email.status !== "pending") {
    toastOrderEmailResult(label, email, myEmail);
    return;
  }
  for (let i = 0; i < MAX_POLLS; i += 1) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    try {
      const { data } = await api.get(path);
      if (data?.email_status && data.email_status !== "pending") {
        toastOrderEmailResult(label, data, myEmail);
        return;
      }
    } catch {
      // keep polling — a transient fetch error says nothing about the email
    }
  }
  toast.info(`${label}: email is still being sent — check the order for its status.`);
};
